import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  Box,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  useMediaQuery,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import styled from "styled-components";
import { useTheme } from "@mui/material/styles";
import logo from "../assets/logo.png";
import logoDetail from "../assets/logo-detail.png";

const NavbarContainer = styled(AppBar)`
  background-color: #0c1c2c;
  padding: 24px 64px;

  @media (max-width: 1024px) {
    padding: 16px 32px;
  }

  @media (max-width: 900px) {
    padding: 12px 24px;
  }

  @media (max-width: 600px) {
    padding: 8px 16px;
  }
`;

const LogoContainer = styled(Box)`
  display: flex;
  align-items: center;
  gap: 0;
  cursor: pointer;
  transition: opacity 0.3s ease;

  &:hover {
    opacity: 0.8;
  }
`;

const Logo = styled.img`
  height: 58px;

  @media (max-width: 1024px) {
    height: 38px;
  }

  @media (max-width: 768px) {
    height: 32px;
  }
`;

const LogoText = styled.img`
  height: 48px;

  @media (max-width: 1024px) {
    height: 36px;
  }

  @media (max-width: 768px) {
    height: 28px;
  }
`;

const NavLinks = styled(Box)`
  display: flex;
  gap: 64px;
  align-items: center;

  @media (max-width: 1200px) {
    gap: 40px;
  }

  @media (max-width: 1024px) {
    gap: 24px;
  }

  @media (max-width: 900px) {
    gap: 16px;
  }
`;

const NavLink = styled.a`
  font-family: "Montserrat", sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;
  text-decoration: none;
  white-space: nowrap;

  @media (max-width: 1024px) {
    font-size: 14px;
  }

  @media (max-width: 900px) {
    font-size: 13px;
  }

  color: ${(props) => (props.active ? "transparent" : "#ffffff")};
  background: ${(props) =>
    props.active
      ? "linear-gradient(90deg, #C69957, #F7E096, #A57632)"
      : "none"};
  -webkit-background-clip: ${(props) => (props.active ? "text" : "unset")};
  background-clip: ${(props) => (props.active ? "text" : "unset")};
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    opacity: 0.8;
  }
`;

const GradientButton = styled(Button)`
  min-width: 130px;
  height: 40px;
  background: linear-gradient(
    106.71deg,
    #c69957 0%,
    #f7e096 47.6%,
    #a57632 93.27%
  ) !important;

  color: #1e1d1b !important;
  font-family: "Montserrat", sans-serif !important;
  font-style: normal !important;
  font-weight: 400 !important;
  font-size: 16px !important;
  line-height: 24px !important;

  text-transform: none !important;
  white-space: nowrap !important;
  letter-spacing: 0 !important;
  text-align: center;
  box-shadow: none !important;
  padding: 8px 16px !important;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  &:hover {
    opacity: 0.9;
    background: linear-gradient(
      106.71deg,
      #c69957 0%,
      #f7e096 47.6%,
      #a57632 93.27%
    ) !important;
    box-shadow: none !important;
  }
`;

export default function Navbar() {
  const [active, setActive] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();

  const navItems = [
    { label: "Home", value: "home" },
    { label: "Features", value: "features" },
    { label: "Market Applications", value: "usecases" },
    // { label: "Testimonials", value: "testimonials" },
    { label: "About Us", value: "about" },
  ];

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const scrollToSection = (sectionId) => {
    if (window.location.hash !== "#/") {
      navigate("/", { replace: false });
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
          setActive(sectionId);
        }
      }, 300);
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
        setActive(sectionId);
      }
    }
  };

  const handleLogoClick = () => {
    scrollToSection("home");
  };

  const drawer = (
    <Box
      sx={{
        width: 250,
        backgroundColor: "#0c1c2c",
        height: "100%",
        paddingTop: "32px",
      }}
    >
      <List>
        {navItems.map(({ label, value }) => (
          <ListItem
            button
            key={value}
            onClick={() => {
              scrollToSection(value);
              setMobileOpen(false);
            }}
            sx={{ cursor: "pointer" }}
          >
            <ListItemText
              primary={label}
              primaryTypographyProps={{
                fontFamily: "Overpass Mono",
                fontWeight: 500,
                fontSize: 16,
                color:
                  active === value ? "transparent" : theme.palette.common.white,
                style:
                  active === value
                    ? {
                        background:
                          "linear-gradient(90deg, #C69957, #F7E096, #A57632)",
                        WebkitBackgroundClip: "text",
                        backgroundClip: "text",
                      }
                    : {},
              }}
            />
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <>
      <NavbarContainer position="fixed" sx={{ zIndex: 1100 }}>
        <Toolbar
          disableGutters
          sx={{ width: "100%", justifyContent: "space-between" }}
        >
          {/* Logo Section (left) - Now Clickable */}
          <LogoContainer onClick={handleLogoClick}>
            <Logo src={logo} alt="Neuora" />
            <LogoText src={logoDetail} alt="Logo" />
          </LogoContainer>

          {/* Centered NavLinks */}
          {!isMobile && (
            <Box sx={{ flex: 1, display: "flex", justifyContent: "center" }}>
              <NavLinks>
                {navItems.map(({ label, value }) => (
                  <NavLink
                    key={value}
                    active={active === value}
                    onClick={() => scrollToSection(value)}
                  >
                    {label}
                  </NavLink>
                ))}
              </NavLinks>
            </Box>
          )}

          {/* Right-side Button/Menu */}
          {isMobile ? (
            <IconButton onClick={handleDrawerToggle} sx={{ color: "#fff" }}>
              <MenuIcon />
            </IconButton>
          ) : (
            <GradientButton
              sx={{ borderRadius: "8px" }}
              onClick={() => {
                setActive("");
                navigate("/contact");
              }}
            >
              <span className="button-text">Contact us</span>
            </GradientButton>
          )}
        </Toolbar>
      </NavbarContainer>

      <Drawer anchor="right" open={mobileOpen} onClose={handleDrawerToggle}>
        {drawer}
      </Drawer>
    </>
  );
}
